// Therapist SOS alerts on the admin side — 0273. Pure shaping of what the card
// shows, kept out of the component so it is tested.
//
// DATA WALL: nothing here reads a client. The card names the therapist (first
// name + surname initial, THER-041) and the booking ref; the location line says
// how fresh HER pin was, never where anyone lives.
//
// ⚠ HER PIN CAN STAND AT A CLIENT'S DOOR. An SOS raised mid-session puts the
// point at the client's home. Leon, 2026-10-01: shown anyway, because it is an
// emergency — and no new path, since hub managers already read staff live
// locations (0025). The map labels it as HER position, never as an address,
// and nothing here may ever join a client field onto it.

import { displayName, type GeoPoint, parseGeographyPoint } from "@bednbody/logic";

export type SosDangerType = "harassment" | "violence" | "medical" | "unsafe_location" | "other";

const DANGER_LABELS: Record<SosDangerType, string> = {
  harassment: "Harassment",
  violence: "Violence",
  medical: "Medical",
  unsafe_location: "Unsafe location",
  other: "Other",
};

export interface SosAlertRow {
  id: string;
  /** The ticket id read out over the phone — CAI-SOS-0042 (0286). Display only, never a key. */
  ref: string;
  raised_at: string;
  danger_type: string | null;
  booking_ids: string[];
  location_captured_at: string | null;
  /** Her live pin when she pressed SOS — whatever shape PostgREST returns. */
  geo: unknown;
  hub_id: string | null;
  hub: { name: string } | null;
  therapist: { full_name: string } | null;
  acknowledged_at: string | null;
  /** Whoever acknowledged it — staff, never a client. */
  handler: { full_name: string } | null;
}

/**
 * Where an SOS stands (0277). `resolved` never reaches the live screens — it is
 * kept in the record and the audit log — so the live views carry the two
 * stages before it.
 *
 *   open          nobody has it yet: red, pulsing
 *   acknowledged  someone has it: stays on screen, calmer, until resolved
 */
export type SosStage = "open" | "acknowledged";

export interface SosAlertView {
  id: string;
  ref: string;
  raisedAt: string;
  therapistName: string;
  hubName: string | null;
  dangerLabel: string | null;
  bookingRefs: string[];
  locationLine: string;
  hubId: string | null;
  /**
   * Where she was when she pressed SOS — the point the live map pins. It stays
   * there and does not follow her — the same snapshot `locationLine` describes,
   * so the pin and the card agree. `null` when there was no
   * live location on record: the card still shows, the map has nothing to pin.
   */
  point: GeoPoint | null;
  stage: SosStage;
  acknowledgedAt: string | null;
  /** First name + surname initial of whoever has it, once acknowledged. */
  handlerName: string | null;
}

export function dangerLabel(value: string | null): string | null {
  return value && value in DANGER_LABELS ? DANGER_LABELS[value as SosDangerType] : null;
}

/**
 * How fresh her pin was when she raised the SOS. It is a snapshot of the last
 * live location, which may be older than the SOS itself — the admin must know
 * how much to trust it.
 */
export function locationLine(raisedAt: string, capturedAt: string | null): string {
  if (!capturedAt) return "No live location on record";
  const seconds = Math.max(
    0,
    Math.round((new Date(raisedAt).getTime() - new Date(capturedAt).getTime()) / 1000),
  );
  if (seconds < 60) return `Location ${seconds} s before the SOS`;
  const minutes = Math.round(seconds / 60);
  return `Location ${minutes} min before the SOS`;
}

export function toSosAlertView(row: SosAlertRow, refsById: Map<string, string>): SosAlertView {
  return {
    id: row.id,
    ref: row.ref,
    raisedAt: row.raised_at,
    therapistName: displayName(row.therapist?.full_name) ?? "A therapist",
    hubName: row.hub?.name ?? null,
    dangerLabel: dangerLabel(row.danger_type),
    bookingRefs: row.booking_ids
      .map((id) => refsById.get(id))
      .filter((ref): ref is string => Boolean(ref)),
    locationLine: locationLine(row.raised_at, row.location_captured_at),
    hubId: row.hub_id,
    point: parseGeographyPoint(row.geo),
    stage: row.acknowledged_at ? "acknowledged" : "open",
    acknowledgedAt: row.acknowledged_at,
    handlerName: row.acknowledged_at ? (displayName(row.handler?.full_name) ?? "Someone") : null,
  };
}
